import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import AppBranding from './AppBranding';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from './AuthContext';
import { 
  ArrowLeft, 
  Mail, 
  Send, 
  Inbox, 
  MessageSquare,
  User,
  Users,
  Shield,
  Trash2,
  Check,
  Clock,
  Search,
  Plus,
  X,
  Megaphone
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const MessagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({
    to_user_id: '',
    subject: '',
    message: ''
  });
  const [replyMessage, setReplyMessage] = useState('');

  const fetchMessages = useCallback(async (folder = 'inbox') => {
    try {
      const response = await axios.get(`${API_V2}/messages?folder=${folder}`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/messages/conversations`, { withCredentials: true });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/messages/users/list`, { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchThread = useCallback(async (otherUserId) => {
    try {
      const response = await axios.get(`${API_V2}/messages/thread/${otherUserId}`, { withCredentials: true });
      setThreadMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    }
  }, []);

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Failed to check admin status:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchMessages('inbox'),
        fetchConversations(),
        fetchUsers(),
        checkAdminStatus()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchMessages, fetchConversations, fetchUsers, checkAdminStatus]);

  useEffect(() => {
    if (activeTab === 'inbox' || activeTab === 'sent') {
      fetchMessages(activeTab);
    }
  }, [activeTab, fetchMessages]);

  useEffect(() => {
    if (selectedConversation) {
      fetchThread(selectedConversation.user_id);
    }
  }, [selectedConversation, fetchThread]);

  const handleSendMessage = async () => {
    if (!composeData.to_user_id || !composeData.message.trim()) {
      toast.error('Please select a recipient and enter a message');
      return;
    }

    try {
      await axios.post(`${API_V2}/messages`, composeData, { withCredentials: true });
      toast.success('Message sent!');
      setShowCompose(false);
      setComposeData({ to_user_id: '', subject: '', message: '' });
      fetchMessages(activeTab);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message', { description: error.response?.data?.detail });
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API_V2}/messages`, {
        to_user_id: selectedConversation.user_id,
        subject: `Re: ${threadMessages[0]?.subject || ''}`,
        message: replyMessage
      }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyMessage('');
      fetchThread(selectedConversation.user_id);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleBroadcast = async () => {
    if (!composeData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const response = await axios.post(`${API_V2}/messages/broadcast`, {
        subject: composeData.subject,
        message: composeData.message
      }, { withCredentials: true });
      toast.success(response.data.message);
      setShowCompose(false);
      setComposeData({ to_user_id: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send broadcast', { description: error.response?.data?.detail });
    }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      await axios.delete(`${API_V2}/messages/${msgId}`, { withCredentials: true });
      toast.success('Message deleted');
      fetchMessages(activeTab);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'admin_broadcast': return <Megaphone className="h-4 w-4 text-yellow-500" />;
      case 'support': return <Shield className="h-4 w-4 text-blue-500" />;
      default: return <User className="h-4 w-4 text-[#00FF41]" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#00FF41] animate-pulse">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <AppBranding />
      <div className="p-4">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              BACK
            </Button>
            <div className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-[#00FF41]" />
              <h1 className="text-2xl font-bold text-[#00FF41] tracking-widest">
                MESSAGES
              </h1>
            </div>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                onClick={() => {
                  setComposeData({ to_user_id: 'broadcast', subject: '', message: '' });
                  setShowCompose(true);
                }}
                className="matrix-btn rounded-none border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                data-testid="broadcast-btn"
              >
                <Megaphone className="h-4 w-4 mr-2" />
                BROADCAST
              </Button>
            )}
            <Button
              onClick={() => {
                setComposeData({ to_user_id: '', subject: '', message: '' });
                setShowCompose(true);
              }}
              className="matrix-btn rounded-none"
              data-testid="compose-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              COMPOSE
            </Button>
          </div>
        </div>
      </header>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <Card className="matrix-card rounded-none border border-[#003B00] w-full max-w-lg mx-4">
            <CardHeader className="border-b border-[#003B00] pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                <Send className="h-4 w-4" />
                {composeData.to_user_id === 'broadcast' ? 'BROADCAST TO ALL USERS' : 'NEW MESSAGE'}
              </CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowCompose(false)}
                className="h-8 w-8 p-0 text-[#00FF41]"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {composeData.to_user_id !== 'broadcast' && (
                <div>
                  <label className="text-[#008F11] text-xs mb-1 block">TO</label>
                  <select
                    value={composeData.to_user_id}
                    onChange={(e) => setComposeData({ ...composeData, to_user_id: e.target.value })}
                    className="w-full h-10 px-3 bg-black border border-[#003B00] text-[#00FF41] rounded-md"
                    data-testid="recipient-select"
                  >
                    <option value="">Select recipient...</option>
                    <option value="admins">📫 Support Team (Admins)</option>
                    {users.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.is_admin ? '👑 ' : ''}{u.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">SUBJECT</label>
                <Input
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  placeholder="Optional subject line"
                  className="bg-black border-[#003B00] text-[#00FF41]"
                  data-testid="subject-input"
                />
              </div>
              
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">MESSAGE *</label>
                <textarea
                  value={composeData.message}
                  onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
                  placeholder="Type your message..."
                  className="w-full h-32 px-3 py-2 bg-black border border-[#003B00] text-[#00FF41] rounded-md resize-none"
                  data-testid="message-input"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCompose(false)}
                  className="border-[#003B00] text-[#008F11]"
                >
                  CANCEL
                </Button>
                <Button
                  onClick={composeData.to_user_id === 'broadcast' ? handleBroadcast : handleSendMessage}
                  className="matrix-btn"
                  data-testid="send-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  SEND
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Panel - Message List / Conversations */}
        <div className="lg:col-span-1">
          <Card className="matrix-card rounded-none border border-[#003B00]">
            <CardHeader className="border-b border-[#003B00] pb-2 px-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-[#001500] border border-[#003B00]">
                  <TabsTrigger 
                    value="inbox" 
                    className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-xs rounded-none"
                  >
                    <Inbox className="h-3 w-3 mr-1" />
                    INBOX
                  </TabsTrigger>
                  <TabsTrigger 
                    value="sent" 
                    className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-xs rounded-none"
                  >
                    <Send className="h-3 w-3 mr-1" />
                    SENT
                  </TabsTrigger>
                  <TabsTrigger 
                    value="conversations" 
                    className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-xs rounded-none"
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    CHATS
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-280px)]">
                {activeTab === 'conversations' ? (
                  // Conversations list
                  conversations.length === 0 ? (
                    <div className="p-4 text-center text-[#008F11] text-sm">No conversations yet</div>
                  ) : (
                    <div className="divide-y divide-[#003B00]">
                      {conversations.map((conv) => (
                        <div
                          key={conv.user_id}
                          onClick={() => setSelectedConversation(conv)}
                          className={`p-3 cursor-pointer hover:bg-[#001500] ${
                            selectedConversation?.user_id === conv.user_id ? 'bg-[#002500]' : ''
                          } ${!conv.is_read ? 'border-l-2 border-[#00FF41]' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-[#008F11]" />
                            <span className="text-[#00FF41] text-sm font-semibold">{conv.user_name}</span>
                          </div>
                          <p className="text-[#006600] text-xs truncate mt-1">{conv.last_message}</p>
                          <p className="text-[#004400] text-xs mt-1">{formatTime(conv.last_message_time)}</p>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  // Inbox/Sent messages list
                  messages.length === 0 ? (
                    <div className="p-4 text-center text-[#008F11] text-sm">No messages</div>
                  ) : (
                    <div className="divide-y divide-[#003B00]">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-3 hover:bg-[#001500] ${!msg.is_read && activeTab === 'inbox' ? 'border-l-2 border-[#00FF41] bg-[#001500]/50' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getMessageTypeIcon(msg.type)}
                              <span className="text-[#00FF41] text-sm font-semibold">
                                {activeTab === 'inbox' ? msg.from_user_name : msg.to_user_name}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:bg-red-900/30"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="text-[#008F11] text-xs font-semibold mt-1">{msg.subject}</p>
                          <p className="text-[#006600] text-xs truncate mt-1">{msg.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-3 w-3 text-[#004400]" />
                            <span className="text-[#004400] text-xs">{formatTime(msg.created_at)}</span>
                            {msg.is_read && activeTab === 'inbox' && (
                              <Check className="h-3 w-3 text-[#008F11]" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Thread View */}
        <div className="lg:col-span-2">
          <Card className="matrix-card rounded-none border border-[#003B00] h-[calc(100vh-200px)]">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b border-[#003B00] pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      CONVERSATION WITH {selectedConversation.user_name?.toUpperCase()}
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedConversation(null)}
                      className="h-8 w-8 p-0 text-[#00FF41]"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 flex flex-col h-[calc(100%-60px)]">
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {threadMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.from_user_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] p-3 rounded ${
                              msg.from_user_id === user?.user_id
                                ? 'bg-[#003B00] border border-[#00FF41]'
                                : 'bg-[#001500] border border-[#003B00]'
                            }`}
                          >
                            <p className="text-[#00FF41] text-sm whitespace-pre-wrap">{msg.message}</p>
                            <p className="text-[#006600] text-xs mt-2">{formatTime(msg.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="p-4 border-t border-[#003B00]">
                    <div className="flex gap-2">
                      <Input
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type a reply..."
                        className="bg-black border-[#003B00] text-[#00FF41] flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                        data-testid="reply-input"
                      />
                      <Button
                        onClick={handleSendReply}
                        className="matrix-btn"
                        disabled={!replyMessage.trim()}
                        data-testid="reply-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-[#008F11]">
                <div className="text-center">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a conversation to view messages</p>
                  <p className="text-xs mt-2 text-[#006600]">or click COMPOSE to start a new one</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MessagesPage;
